/**
 * Mock data generators for Company entities.
 *
 * Security Note: Math.random() is used throughout this file for generating
 * test/mock data only. This file is excluded from production bundles and
 * is not used for any security-sensitive operations.
 * All Math.random() usages here are safe (NOSONAR).
 */
import { Company, CompanySearchResult } from '../../core/models/company.model';

const companyNames = [
  'Acme Corporation',
  'Global Industries',
  'Tech Solutions Inc',
  'Premier Services LLC',
  'United Manufacturing',
  'Pacific Holdings',
  'Atlantic Partners',
  'Mountain View Corp',
  'Sunrise Enterprises',
  'Golden State LLC',
  'Blue Ocean Co',
  'Green Valley Inc',
  'Silver Creek Partners',
  'Red Rock Holdings',
  'White Plains Corp',
  'Black Diamond LLC',
  'Horizon Industries',
  'Summit Group',
  'Valley Forge Inc',
  'Coastal Ventures',
];

const customerTypes = [
  'Enterprise',
  'SMB',
  'Government',
  'Non-Profit',
  'Startup',
];
const customerTypeIds = [1, 2, 3, 4, 5];
const printSites = ['Site A - East', 'Site B - West', 'Site C - Central', null];

function randomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function generateArNumber(index: number): string {
  return `AR${String(index + 1000).padStart(6, '0')}`;
}

export function generateMockCompany(index: number): Company {
  const baseName = companyNames[index % companyNames.length];
  const suffix =
    index > companyNames.length
      ? ' ' + Math.floor(index / companyNames.length)
      : '';
  const name = baseName + suffix;

  return {
    arNumber: generateArNumber(index),
    name,
    type: randomItem(customerTypes),
    preferredPrintSite: randomItem(printSites) ?? undefined,
    customerTypeId: randomItem(customerTypeIds),
    masterCompanyCode: Math.random() > 0.7 ? `MC${index}` : null,
    outsourceConsent: Math.random() > 0.5,
    preferPrintSiteId: Math.floor(Math.random() * 3) + 1,
    isOutsourceOnly: Math.random() > 0.8,
    isUltiBank: Math.random() > 0.9,
    isPackingDetailDistribution: Math.random() > 0.8,
    checkLayoutId: Math.floor(Math.random() * 4) + 1,
    isBackOfficeSort: Math.random() > 0.7,
    isCustom: Math.random() > 0.85,
    isShippingUpCharge: Math.random() > 0.75,
    isManualCheck: Math.random() > 0.8,
    isGroupRequests: Math.random() > 0.7,
    customProcessFileName: Math.random() > 0.8 ? `process_${index}.cfg` : null,
    comments: Math.random() > 0.6 ? `Notes for company ${index + 1}` : null,
  };
}

export function generateMockCompanies(count = 100): Company[] {
  return Array.from({ length: count }, (_, i) => generateMockCompany(i + 1));
}

// Pre-generated mock data for consistent testing
let cachedCompanies: Company[] | null = null;

export function getMockCompanies(): Company[] {
  cachedCompanies ??= generateMockCompanies(100);
  return cachedCompanies;
}

export function searchMockCompanies(
  query: string,
  pageSize = 3,
  pageNumber = 1
): {
  items: CompanySearchResult[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
} {
  const companies = getMockCompanies();
  const queryLower = query.toLowerCase();

  const filtered = companies.filter(
    c =>
      c.arNumber.toLowerCase().includes(queryLower) ||
      c.name.toLowerCase().includes(queryLower)
  );

  const totalCount = filtered.length;
  const startIndex = (pageNumber - 1) * pageSize;
  const items: CompanySearchResult[] = filtered
    .slice(startIndex, startIndex + pageSize)
    .map(c => ({
      id: c.arNumber, // Use arNumber as the ID for search results
      arNumber: c.arNumber,
      name: c.name,
      customerType: c.type ?? undefined,
    }));

  return {
    items,
    totalCount,
    pageNumber,
    pageSize,
  };
}

export function getMockCompanyById(id: string): Company | null {
  const companies = getMockCompanies();
  // Search by arNumber since that's the primary identifier
  return companies.find(c => c.arNumber === id) ?? null;
}

export function getMockCompanyByArNumber(arNumber: string): Company | null {
  const companies = getMockCompanies();
  return (
    companies.find(c => c.arNumber.toLowerCase() === arNumber.toLowerCase()) ??
    null
  );
}

export function createMockCompany(company: Company): Company {
  if (cachedCompanies) {
    cachedCompanies.push(company);
  }
  return company;
}

export function updateMockCompany(
  id: string,
  updates: Partial<Company>
): Company | null {
  cachedCompanies ??= generateMockCompanies(100);

  // Find by arNumber since that's the identifier
  const index = cachedCompanies.findIndex(c => c.arNumber === id);
  if (index === -1) {
    return null;
  }

  cachedCompanies[index] = { ...cachedCompanies[index], ...updates };
  return cachedCompanies[index];
}

export function checkArNumberExists(
  arNumber: string,
  excludeArNumber?: string
): boolean {
  const companies = getMockCompanies();
  return companies.some(
    c =>
      c.arNumber.toLowerCase() === arNumber.toLowerCase() &&
      c.arNumber !== excludeArNumber
  );
}

export function getPaginatedMockCompanies(
  pageSize = 10,
  pageNumber = 1
): {
  items: Company[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
} {
  const companies = getMockCompanies();
  const totalCount = companies.length;
  const startIndex = (pageNumber - 1) * pageSize;
  const items = companies.slice(startIndex, startIndex + pageSize);

  return {
    items,
    totalCount,
    pageNumber,
    pageSize,
  };
}
